import React, { useContext, useMemo } from 'react';
import { Flex, useSafeArea, useScreenSize } from '@banhala/ui-kit';
import Animated, {
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import {
  ScrollToTopButton,
  PromotionFloatingButton,
} from 'shared/src/component';
import { isAndroid } from 'shared/src/utils';
import type { FloatingButton } from 'shared/src/api/promotionsService/getPromotionComponents/types';
import { HomeScrollContext } from '../../context/HomeScrollContext';
import { useHomePromotionComponents } from '../../queries/useHomePromotionComponents';
import { useLogClickHomeFloatingButton } from '../../hooks/useLogClickHomeFloatingButton';
import { useLogClickScrollToTop } from '../../hooks/useLogClickScrollToTop';

// GoodsDetail FloatingButtons 이식본(홈용). GoodsDetail 은 ScrollContext(listRef/scrollY)에
// 강결합돼 있어 그대로 못 가져오므로, HomeScrollContext(scrollY/scrollToTop)와 홈 프로모션
// 훅(useHomePromotionComponents/useLogClickHomeFloatingButton)으로 배선했다.
// - 스크롤 투 탑: HomeScrollContext.scrollToTop()
// - 프로모션: 홈 promotion(screen=TODAY) floating_buttons
// - 스크롤 투 바텀: 홈에 scrollToEnd 인프라가 없어 제외(필요 시 탭별 scrollToEnd 등록 추가).
const FIXED_OFFSET = 16;
const BUTTON_SIZE = 40;
const VISIBLE_THRESHOLD = 200; // 이만큼 스크롤하면 버튼 노출
const ANIM_DURATION = 500;

/**
 * scrollY 가 threshold 를 넘는 시점에만 withTiming 을 실행(0↔1). GoodsDetail
 * useScrollThresholdValue 이식본 — context 대신 scrollY 를 인자로 받는다.
 */
const useScrollThresholdValue = (
  scrollY: SharedValue<number> | undefined,
  threshold: number,
  duration: number,
) => {
  const animatedValue = useSharedValue(0);
  useAnimatedReaction(
    () => (scrollY?.value ?? 0) > threshold,
    (isOver, prev) => {
      if (isOver !== prev) {
        animatedValue.value = withTiming(isOver ? 1 : 0, { duration });
      }
    },
  );
  return animatedValue;
};

const ButtonContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Flex
    shadowStep={'medium'}
    width={BUTTON_SIZE}
    height={BUTTON_SIZE}
    borderRadius={BUTTON_SIZE * 0.5}
    pointerEvents={'auto'}
  >
    {children}
  </Flex>
);

const HomePromotionFloatingButton: React.FC<{
  button: FloatingButton;
  promotionSno?: number;
}> = ({ button, promotionSno }) => {
  const onClickLog = useLogClickHomeFloatingButton(button, 0);
  return (
    <PromotionFloatingButton
      button={button}
      promotionSno={promotionSno}
      onClickLog={onClickLog}
    />
  );
};

export const HomeFloatingButtons: React.FC = () => {
  const ctx = useContext(HomeScrollContext);
  const { bottom } = useSafeArea();
  const { data } = useHomePromotionComponents();
  const { width: screenWidth } = useScreenSize();
  const logClickScrollToTop = useLogClickScrollToTop();

  const visibilityValue = useScrollThresholdValue(
    ctx?.scrollY,
    VISIBLE_THRESHOLD,
    ANIM_DURATION,
  );

  const floatingButton = useMemo(() => {
    const buttons = data?.component?.floating_buttons ?? [];
    if (buttons.length === 0) return null;
    return {
      button: buttons[Math.floor(Math.random() * buttons.length)],
      promotionSno: data?.component?.promotion_sno,
    };
  }, [data?.component?.floating_buttons, data?.component?.promotion_sno]);

  // worklet 이 SharedValue 만 캡처하도록 원시값만 참조(children/props 캡처 금지).
  const style = useAnimatedStyle(() => ({
    alignItems: 'flex-end',
    gap: 12,
    // pointerEvents: visibilityValue.value > 0.5 ? 'auto' : 'none',
    opacity: isAndroid
      ? visibilityValue.value > 0.5
        ? 1
        : 0
      : visibilityValue.value,
    width: screenWidth,
    paddingRight: 12,
  }));

  return (
    <Flex
      alignItems={'center'}
      position={'absolute'}
      left={0}
      right={0}
      pointerEvents={'none'}
      bottom={bottom + FIXED_OFFSET + 32}
    >
      <Animated.View style={style}>
        <ButtonContainer>
          <ScrollToTopButton
            testID={'탑버튼'}
            onPress={() => {
              // 로깅 먼저, 동작 나중(스크롤로 리렌더가 겹쳐도 이벤트가 유실되지 않게).
              logClickScrollToTop();
              ctx?.scrollToTop();
            }}
          />
        </ButtonContainer>
        {floatingButton && (
          <HomePromotionFloatingButton
            button={floatingButton.button}
            promotionSno={floatingButton.promotionSno}
          />
        )}
      </Animated.View>
    </Flex>
  );
};
